import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAdminExecutionDetail } from '@/hooks/useAdminAutomations';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { ExecutionTimeline } from '../components/ExecutionTimeline';
import { ExecutionLogsViewer } from '../components/ExecutionLogsViewer';
import { RetryButton } from '../components/RetryButton';
import { ExecutionStatusSummary } from '../components/ExecutionStatusSummary';
import { EffectContractSummary } from '../components/EffectContractSummary';
import {
  shouldOfferManualRetry,
  shouldShowTechnicalDetail,
} from '../utils/automation-execution-presentation';
import { formatDateTime, formatDuration } from '@/utils/formatters';

export function ExecutionDetailPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { data: execution, isLoading } = useAdminExecutionDetail(uuid);

  if (isLoading)
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  if (!execution)
    return (
      <div className="py-12 text-center text-neutral-600 dark:text-neutral-400">
        Exécution introuvable
      </div>
    );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/automations')}
          />
          <div>
            <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
              {execution.templateName}
            </h1>
            <p className="text-sm text-neutral-500">
              {execution.domainName} / {execution.folderName}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:justify-end">
          <ExecutionStatusSummary execution={execution} compact />
          {shouldOfferManualRetry(execution) && <RetryButton executionUuid={execution.uuid} />}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardContent>
            <p className="text-xs text-neutral-500">Début</p>
            <p className="text-sm text-neutral-900 dark:text-neutral-200">
              {formatDateTime(execution.startedAt)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-xs text-neutral-500">Durée</p>
            <p className="text-sm text-neutral-900 dark:text-neutral-200">
              {execution.duration != null ? formatDuration(execution.duration) : '--'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-xs text-neutral-500">Organisation</p>
            <p className="text-sm text-neutral-900 dark:text-neutral-200">
              {execution.organisationName}
            </p>
          </CardContent>
        </Card>
      </div>

      {execution.status === 'cancelled' && <ExecutionStatusSummary execution={execution} />}

      <EffectContractSummary execution={execution} />

      {shouldShowTechnicalDetail(execution) && (
        <div className="rounded-lg border border-danger-500/25 bg-danger-500/10 p-4">
          <p className="mb-1 text-xs font-medium text-danger-700 dark:text-danger-400">
            {execution.status === 'failed' || execution.status === 'error'
              ? 'Erreur'
              : 'Détail technique'}
          </p>
          <p className="font-mono text-sm text-danger-700 dark:text-danger-300">
            {execution.error}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader title="Étapes" />
          <CardContent>
            <ExecutionTimeline steps={execution.steps || []} />
          </CardContent>
        </Card>
        <div className="lg:col-span-2">
          <ExecutionLogsViewer logs={execution.logs || []} />
        </div>
      </div>

      {execution.screenshots && execution.screenshots.length > 0 && (
        <Card>
          <CardHeader
            title="Captures d'écran"
            description={`${execution.screenshots.length} capture(s)`}
          />
          <CardContent>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {execution.screenshots.map((url, i) => (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-700 hover:border-primary-500 transition-colors"
                >
                  <img src={url} alt={`Screenshot ${i + 1}`} className="w-full h-auto" />
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
